import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Divider,
  Stack,
  LinearProgress,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import InfoIcon from "@mui/icons-material/Info";
import UpdateIcon from "@mui/icons-material/Update";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonIcon from "@mui/icons-material/Person";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Header from "../../../components/Header";
import Menu from "../../../components/Menu";
import Footer from "../../../components/Footer";
import ContentContainer from "../../../components/ContentContainer";
import { UserContext } from "../../../contexts/UserContext";
import { useSnackbar } from "notistack";

const DocumentStatusPage = () => {
  const { token, roleId } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();

  const [searchRut, setSearchRut] = useState("");
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [updatingStatuses, setUpdatingStatuses] = useState(false);
  const [error, setError] = useState("");
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [loadingDocument, setLoadingDocument] = useState(false);

  // Estado para controlar el flujo de búsqueda avanzada
  const [searchState, setSearchState] = useState({
    checking: false,
    notFoundInDb: false,
    foundInWisphub: false,
    notFoundAnywhere: false,
    clientData: null,
  });

  // Efecto para expandir el menú al cargar
  useEffect(() => {
    sessionStorage.setItem("menuAdminExpanded", "true");
  }, []);

  // Efecto para actualizar los documentos filtrados cuando cambia la lista de documentos
  useEffect(() => {
    if (documents.length > 0) {
      // Ordenar por fecha de creación (más reciente primero)
      const sorted = [...documents].sort((a, b) => {
        const dateA = a.sovos_created_at
          ? new Date(a.sovos_created_at)
          : a.sovosData?.created_at
          ? new Date(a.sovosData.created_at)
          : new Date(0);
        const dateB = b.sovos_created_at
          ? new Date(b.sovos_created_at)
          : b.sovosData?.created_at
          ? new Date(b.sovosData.created_at)
          : new Date(0);

        return dateB - dateA;
      });

      setFilteredDocuments(sorted);
    }
  }, [documents]);

  // Efecto para limpiar los resultados de búsqueda cuando se cambia el RUT
  const resetSearchResults = useCallback(() => {
    // Solo resetear si se ha realizado una búsqueda previa
    if (searchPerformed) {
      setDocuments([]);
      setFilteredDocuments([]);
      setSearchPerformed(false);
      setSearchState({
        checking: false,
        notFoundInDb: false,
        foundInWisphub: false,
        notFoundAnywhere: false,
        clientData: null,
      });
    }
  }, [searchPerformed]);

  // Función para verificar si un documento es reciente (menos de 2 días)
  const isRecent = useCallback((dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const now = new Date();
    return now - date < 1000 * 60 * 60 * 24 * 2; // 2 días
  }, []);

  const handleMenuClick = (action) => {
    switch (action) {
      case "Ventas":
        window.location.href = "/ventas";
        break;
      case "Ingresar venta":
        window.location.href = "/ingresar-venta";
        break;
      default:
        console.log("Acción del menú:", action);
        break;
    }
  };

  const formatRut = (rut) => {
    // Obtener el valor actual para comparar después
    const currentRut = searchRut;

    // Formatear el RUT
    const formattedRut = rut.replace(/[^0-9kK]/g, "").slice(0, 9);
    if (formattedRut.length <= 1) return formattedRut;
    const result = `${formattedRut.slice(0, -1)}-${formattedRut.slice(-1)}`;

    // Si el RUT ha cambiado, resetear los resultados de búsqueda
    if (currentRut !== result) {
      resetSearchResults();
    }

    return result;
  };

  // Función para buscar cliente en Wisphub
  const checkClientInWisphub = async (clientRut) => {
    try {
      console.log("Buscando cliente en Wisphub...", clientRut);

      // Hacer la solicitud a la API proxy de Wisphub
      const wisphubUrl = `${
        process.env.REACT_APP_API_URL
      }/proxy/clientes?buscar=${clientRut.trim()}`;

      const wisphubResponse = await fetch(wisphubUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Si la respuesta no es exitosa (como 401, 404, etc.)
      if (!wisphubResponse.ok) {
        console.error(`Error en respuesta Wisphub: ${wisphubResponse.status}`);
        // Comprobación especial para 401 Unauthorized
        if (wisphubResponse.status === 401) {
          enqueueSnackbar(
            "Error de autenticación con Wisphub. Contacte al administrador.",
            {
              variant: "error",
            }
          );
        }
        return { success: false };
      }

      const wisphubData = await wisphubResponse.json();

      // Verificar si hay resultados
      if (
        wisphubData &&
        wisphubData.results &&
        wisphubData.results.length > 0
      ) {
        // Filtrar clientes que coinciden con el RUT
        const matchingClients = wisphubData.results.filter(
          (client) =>
            client.cedula === clientRut ||
            client.cedula === clientRut.replace("-", "")
        );

        if (matchingClients.length > 0) {
          return {
            success: true,
            client: matchingClients[0],
          };
        }
      }

      // Si no se encontró ningún cliente que coincida
      return { success: false };
    } catch (error) {
      console.error("Error al consultar Wisphub:", error);
      return { success: false, error };
    }
  };

  const handleSearchByRut = async () => {
    if (!searchRut.trim()) {
      enqueueSnackbar("Debes ingresar el RUT del cliente.", {
        variant: "warning",
      });
      return;
    }

    setLoading(true);
    setError("");
    setSearchPerformed(true);
    setSearchState({
      checking: true,
      notFoundInDb: false,
      foundInWisphub: false,
      notFoundAnywhere: false,
      clientData: null,
    });

    try {
      // Primera búsqueda en la base de datos local
      const url = `${
        process.env.REACT_APP_API_URL
      }/digital-contracts/by-client-rut?clientRut=${searchRut.trim()}`;

      const response = await fetch(url);
      const data = await response.json();

      console.log("Documentos encontrados:", data.documents || data.contracts);

      if (
        data.success &&
        ((data.contracts && data.contracts.length > 0) ||
          (data.documents && data.documents.length > 0))
      ) {
        // Si encuentra contratos, muestra normalmente
        const documentsToUse = data.contracts || data.documents;
        setDocuments(documentsToUse);
        setFilteredDocuments(documentsToUse);
        await updateDocumentStatuses(documentsToUse);

        // Resetear estados de búsqueda
        setSearchState({
          checking: false,
          notFoundInDb: false,
          foundInWisphub: false,
          notFoundAnywhere: false,
          clientData: null,
        });
      } else {
        // Si no encuentra contratos, buscar en Wisphub
        setDocuments([]);
        setFilteredDocuments([]);

        console.log(
          "No se encontraron documentos en BD local, verificando en Wisphub"
        );
        const wisphubResult = await checkClientInWisphub(searchRut);

        if (wisphubResult.success && wisphubResult.client) {
          // Cliente encontrado en Wisphub pero sin contratos digitales
          console.log("Cliente encontrado en Wisphub:", wisphubResult.client);
          setSearchState({
            checking: false,
            notFoundInDb: true,
            foundInWisphub: true,
            notFoundAnywhere: false,
            clientData: wisphubResult.client,
          });
        } else {
          // Cliente no encontrado en ninguna parte
          console.log("Cliente no encontrado en ningún sistema");
          setSearchState({
            checking: false,
            notFoundInDb: true,
            foundInWisphub: false,
            notFoundAnywhere: true,
            clientData: null,
          });
        }
      }
    } catch (err) {
      console.error("Error al buscar documentos por RUT:", err);
      setError("Error al consultar documentos.");
      setSearchState({
        checking: false,
        notFoundInDb: true,
        foundInWisphub: false,
        notFoundAnywhere: true,
        clientData: null,
      });
    } finally {
      setLoading(false);
    }
  };

  // Función para limpiar la búsqueda
  const handleClearSearch = () => {
    setSearchRut("");
    resetSearchResults();
  };

  // Función para actualizar estados de documentos desde SOVOS
  const updateDocumentStatuses = async (docs) => {
    if (!docs || docs.length === 0) return;

    setUpdatingStatuses(true);

    try {
      const updatedDocs = [...docs];
      for (let i = 0; i < updatedDocs.length; i++) {
        const doc = updatedDocs[i];
        if (!doc.document_code) continue;

        try {
          // Consultar estado en SOVOS
          const statusUrl = `${process.env.REACT_APP_API_URL}/digital-contracts/status?documentCode=${doc.document_code}`;
          const statusResponse = await fetch(statusUrl);

          // Comprobar si la respuesta es JSON válido
          const contentType = statusResponse.headers.get("content-type");
          if (!contentType || !contentType.includes("application/json")) {
            console.warn(
              `Respuesta no JSON para documento ${doc.document_code}`
            );
            continue; // Salta a la siguiente iteración
          }

          const statusData = await statusResponse.json();
          console.log(`Estado para ${doc.document_code}:`, statusData);

          if (statusData.success) {
            // Determinar el nuevo estado basado en la respuesta de SOVOS
            let newStatus = "Pendiente";

            // Obtener datos de firmantes desde 'result' o 'status'
            const sovosData = statusData.result || statusData.status;

            // Guardar datos de Sovos para uso en ordenamiento y visualización
            updatedDocs[i].sovosData = sovosData;

            // Extraer la fecha de creación de Sovos si está disponible
            if (sovosData && sovosData.created_at) {
              updatedDocs[i].sovos_created_at = sovosData.created_at;
            } else if (sovosData && sovosData.date) {
              updatedDocs[i].sovos_created_at = sovosData.date;
            }

            if (sovosData && sovosData.signers) {
              // Solo considerar firmantes reales (who_signs: 1) y no auditores/visualizadores
              const realSigners = sovosData.signers.filter(
                (s) => s.who_signs === 1 || s.who_signs === "1"
              );
              const totalRealSigners = realSigners.length;
              const signedCount = realSigners.filter(
                (s) => Number(s.state) === 1
              ).length;

              // Guardar información de firmantes para mostrar progreso
              updatedDocs[i].signingProgress = {
                signed: signedCount,
                total: totalRealSigners,
                percentage:
                  totalRealSigners > 0
                    ? Math.round((signedCount / totalRealSigners) * 100)
                    : 0,
              };

              // Guardar la fecha de última actualización
              updatedDocs[i].lastUpdated = new Date().toISOString();

              console.log(
                `Documento ${doc.document_code}: ${signedCount}/${totalRealSigners} firmantes reales`
              );

              if (signedCount === 0) {
                newStatus = "Pendiente";
              } else if (signedCount < totalRealSigners) {
                newStatus = "En Proceso de Firma";
              } else if (signedCount === totalRealSigners) {
                newStatus = "Firmado";

                // Si está firmado por todos y no tenemos el archivo,
                // intentamos descargarlo automáticamente
                if (!doc.file_base64) {
                  const downloadUrl = `${process.env.REACT_APP_API_URL}/digital-contracts/signed-download?documentCode=${doc.document_code}&clientRut=${searchRut}`;
                  try {
                    const downloadResponse = await fetch(downloadUrl);
                    const downloadData = await downloadResponse.json();

                    if (downloadData.success && downloadData.file_base64) {
                      updatedDocs[i].file_base64 = downloadData.file_base64;
                    } else if (sovosData.file_preview) {
                      // Si no hay base64 pero hay vista previa, guardamos la URL
                      updatedDocs[i].file_preview = sovosData.file_preview;
                    }
                  } catch (downloadErr) {
                    console.error(`Error al descargar: ${downloadErr.message}`);
                    // Si hay un error en la descarga pero hay vista previa, la guardamos
                    if (sovosData.file_preview) {
                      updatedDocs[i].file_preview = sovosData.file_preview;
                    }
                  }
                }
              }
            }

            console.log(`Actual: ${doc.status}, Nuevo posible: ${newStatus}`);

            // Actualizar el estado en la BD
            if (newStatus !== doc.status) {
              const updateUrl = `${process.env.REACT_APP_API_URL}/digital-contracts/update-status`;
              await fetch(updateUrl, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  documentCode: doc.document_code,
                  clientRut: searchRut,
                  status: newStatus,
                }),
              });

              // Actualizar el estado en el arreglo local
              updatedDocs[i].status = newStatus;
            }
          }
        } catch (statusError) {
          console.error(
            `Error al actualizar estado de documento ${doc.document_code}:`,
            statusError
          );
        }
      }

      // Actualizar la lista de documentos con los estados actualizados
      setDocuments(updatedDocs);
    } catch (err) {
      console.error("Error al actualizar estados de documentos:", err);
    } finally {
      setUpdatingStatuses(false);
    }
  };

  // Actualizar estado de un documento individual
  const updateSingleDocumentStatus = async (documentCode) => {
    const doc = documents.find((d) => d.document_code === documentCode);
    if (!doc) return;

    // Usar el mismo proceso pero solo para un documento
    await updateDocumentStatuses([doc]);

    enqueueSnackbar("Estado actualizado", {
      variant: "success",
      autoHideDuration: 2000,
    });
  };

  // Función para expandir/contraer fila
  const toggleRowExpansion = useCallback((documentCode) => {
    setExpandedRows((prev) => ({
      ...prev,
      [documentCode]: !prev[documentCode],
    }));
  }, []);

  // Búsqueda al presionar Enter
  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSearchByRut();
    }
  };

  // Función para visualizar documento
  const handleViewDocument = async (documentCode, clientRut) => {
    setLoadingDocument(true);
    try {
      // Mostrar diálogo informativo
      enqueueSnackbar("Abriendo documento en una nueva ventana...", {
        variant: "info",
        autoHideDuration: 3000,
      });

      // Obtener el estado para conseguir la URL de vista previa
      const statusUrl = `${process.env.REACT_APP_API_URL}/digital-contracts/status?documentCode=${documentCode}`;
      const statusRes = await fetch(statusUrl);
      const statusData = await statusRes.json();

      // Buscar URL de vista previa
      const docPreviewUrl =
        statusData.result?.file_preview || statusData.status?.file_preview;

      if (docPreviewUrl) {
        // Abrir en nueva ventana
        window.open(docPreviewUrl, "_blank");
        setLoadingDocument(false);
        return;
      }

      // Si no hay URL, intentar la descarga normal
      const url = `${process.env.REACT_APP_API_URL}/digital-contracts/signed-download?documentCode=${documentCode}&clientRut=${clientRut}`;
      const res = await fetch(url);
      const data = await res.json();

      if (data.success && data.file_base64) {
        // Crear un objeto blob y abrir en nueva ventana
        const byteCharacters = atob(data.file_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
      } else {
        enqueueSnackbar("No se pudo cargar el documento.", {
          variant: "warning",
          autoHideDuration: 5000,
        });
      }
    } catch (err) {
      console.error("Error al cargar documento:", err);
      enqueueSnackbar("Error al recuperar el documento firmado.", {
        variant: "error",
        autoHideDuration: 5000,
      });
    } finally {
      setLoadingDocument(false);
    }
  };

  // Función para descargar documento
  const handleDownload = async (documentCode, clientRut) => {
    setLoading(true);
    try {
      // Primero verificamos si podemos usar la vista previa
      const statusUrl = `${process.env.REACT_APP_API_URL}/digital-contracts/status?documentCode=${documentCode}`;
      const statusRes = await fetch(statusUrl);
      const statusData = await statusRes.json();

      const docPreviewUrl =
        statusData.result?.file_preview || statusData.status?.file_preview;

      if (docPreviewUrl) {
        // Si tenemos URL de vista previa, descargamos desde ahí
        const response = await fetch(docPreviewUrl);
        if (!response.ok) {
          throw new Error(`Error HTTP ${response.status}`);
        }

        // Crear un blob y descargarlo
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `documento_${documentCode}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        enqueueSnackbar("Documento descargado con éxito", {
          variant: "success",
          autoHideDuration: 3000,
          ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
        });
        setLoading(false);
        return;
      }

      // Si no hay vista previa, intentamos la ruta normal
      const url = `${process.env.REACT_APP_API_URL}/digital-contracts/signed-download?documentCode=${documentCode}&clientRut=${clientRut}`;
      const res = await fetch(url);
      const data = await res.json();

      if (data.success && data.file_base64) {
        const link = document.createElement("a");
        link.href = "data:application/pdf;base64," + data.file_base64;
        link.download = `documento_${documentCode}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        enqueueSnackbar("Documento descargado con éxito", {
          variant: "success",
          autoHideDuration: 3000,
          ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
        });
      } else {
        enqueueSnackbar("Documento aún no disponible.", {
          variant: "warning",
          autoHideDuration: 5000,
        });
      }
    } catch (err) {
      console.error("Error al descargar documento:", err);
      setError("Error al descargar documento firmado.");
      enqueueSnackbar("Error al descargar documento.", {
        variant: "error",
        autoHideDuration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener color del chip según estado
  const getStatusChipColor = useMemo(
    () => (status) => {
      switch (status) {
        case "Firmado":
          return "success";
        case "En Proceso de Firma":
          return "warning";
        case "Rechazado":
          return "error";
        default:
          return "default";
      }
    },
    []
  );

  // Función para refrescar estados manualmente
  const handleRefreshStatuses = () => {
    updateDocumentStatuses(documents);
  };

  // Función para formatear fecha relativa
  const formatRelativeTime = useCallback((dateString) => {
    if (!dateString) return "No actualizado";

    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;

    // Menos de un minuto
    if (diff < 60000) {
      return "Justo ahora";
    }

    // Menos de una hora
    if (diff < 3600000) {
      const minutes = Math.floor(diff / 60000);
      return `Hace ${minutes} ${minutes === 1 ? "minuto" : "minutos"}`;
    }

    // Menos de un día
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `Hace ${hours} ${hours === 1 ? "hora" : "horas"}`;
    }

    // Más de un día
    const days = Math.floor(diff / 86400000);
    return `Hace ${days} ${days === 1 ? "día" : "días"}`;
  }, []);

  // Función para formatear fecha completa
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "No disponible";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("es-CL", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      console.error("Error al formatear fecha:", error);
      return "Fecha inválida";
    }
  }, []);

  // Icono para el estado
  const getStatusIcon = useCallback((status) => {
    switch (status) {
      case "Firmado":
        return <CheckCircleIcon sx={{ color: "success.main" }} />;
      case "En Proceso de Firma":
        return <HourglassEmptyIcon sx={{ color: "warning.main" }} />;
      case "Rechazado":
        return <CloseIcon sx={{ color: "error.main" }} />;
      default:
        return <HourglassEmptyIcon sx={{ color: "text.secondary" }} />;
    }
  }, []);

  // Componente para mensaje de cliente encontrado en Wisphub pero sin contrato digital
  const ClientFoundInWisphubMessage = ({ clientData }) => {
    return (
      <Paper
        elevation={2}
        sx={{
          p: 4,
          mt: 3,
          textAlign: "center",
          borderRadius: 2,
          backgroundColor: "#f8f9fa",
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          transition: "all 0.3s ease",
          animation: "fadeIn 0.5s ease-in-out",
          "@keyframes fadeIn": {
            "0%": {
              opacity: 0,
              transform: "translateY(20px)",
            },
            "100%": {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
        }}
      >
        <Box sx={{ mb: 2 }}>
          <PersonIcon sx={{ fontSize: 80, color: "#99235c", opacity: 0.8 }} />
        </Box>
        <Typography
          variant="h5"
          sx={{ color: "#99235c", mb: 2, fontWeight: "500" }}
        >
          Cliente encontrado en el sistema
        </Typography>
        <Typography
          variant="body1"
          sx={{ mb: 3, color: "#555", maxWidth: "600px", mx: "auto" }}
        >
          Este cliente existe en nuestro sistema pero no cuenta con un{" "}
          <b>Contrato Digital</b> registrado.
        </Typography>

        {clientData && (
          <Box
            sx={{
              mt: 3,
              p: 3,
              backgroundColor: "rgba(0, 0, 0, 0.02)",
              borderRadius: 1,
              display: "inline-block",
              textAlign: "left",
              maxWidth: "400px",
              width: "100%",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: "#666", mb: 2, fontWeight: 600 }}
            >
              Información del cliente:
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", color: "#555" }}
                >
                  Nombre:
                </Typography>
                <Typography variant="body2" sx={{ color: "#333" }}>
                  {clientData.nombre || "No disponible"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", color: "#555" }}
                >
                  RUT:
                </Typography>
                <Typography variant="body2" sx={{ color: "#333" }}>
                  {clientData.cedula || searchRut}
                </Typography>
              </Box>

              {clientData.email && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", color: "#555" }}
                  >
                    Email:
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#333" }}>
                    {clientData.email}
                  </Typography>
                </Box>
              )}

              {clientData.telefono && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", color: "#555" }}
                  >
                    Teléfono:
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#333" }}>
                    {clientData.telefono}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Paper>
    );
  };

  // Componente para mensaje de cliente no encontrado en ninguna parte
  const ClientNotFoundMessage = () => {
    return (
      <Paper
        elevation={2}
        sx={{
          p: 4,
          mt: 3,
          textAlign: "center",
          borderRadius: 2,
          backgroundColor: "#f8f9fa",
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          transition: "all 0.3s ease",
          animation: "fadeIn 0.5s ease-in-out",
          "@keyframes fadeIn": {
            "0%": {
              opacity: 0,
              transform: "translateY(20px)",
            },
            "100%": {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
        }}
      >
        <Box sx={{ mb: 2 }}>
          <CancelIcon sx={{ fontSize: 80, color: "#ff6b6b", opacity: 0.8 }} />
        </Box>
        <Typography
          variant="h5"
          sx={{ color: "#ff6b6b", mb: 2, fontWeight: "500" }}
        >
          Cliente no encontrado
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mb: 2,
            color: "#555",
            maxWidth: "600px",
            mx: "auto",
            fontSize: "1rem",
            lineHeight: 1.6,
          }}
        >
          No se encontró ningún cliente con el RUT <b>{searchRut}</b> en
          nuestros sistemas.
        </Typography>
        <Box
          sx={{
            p: 3,
            backgroundColor: "rgba(255, 107, 107, 0.05)",
            borderRadius: 1,
            maxWidth: "500px",
            mx: "auto",
            mt: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#666",
              fontSize: "0.9rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <ErrorOutlineIcon fontSize="small" sx={{ color: "#ff6b6b" }} />
            Verifique que el RUT sea correcto o contacte al administrador si
            necesita registrar este cliente.
          </Typography>
        </Box>
      </Paper>
    );
  };

  return (
    <div className="dashboard-container">
      <Header />
      <div className="content-area">
        <Menu
          role_id={roleId}
          onMenuClick={handleMenuClick}
          className="menu-sidebar"
        />
        <div className="main-content">
          <ContentContainer>
            <Typography
              variant="h5"
              sx={{
                color: "#99235c",
                mb: 3,
                fontWeight: 500,
                fontSize: "1.75rem",
              }}
            >
              Consulta Documentos
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 3,
                backgroundColor: "#fff",
                p: 3,
                borderRadius: 2,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <TextField
                label="RUT Cliente (ej: 12345678-9)"
                variant="outlined"
                value={searchRut}
                onChange={(e) => setSearchRut(formatRut(e.target.value))}
                onKeyDown={handleKeyPress}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#99235c",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#99235c",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#99235c",
                  },
                }}
              />
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#99235c",
                  minWidth: "120px",
                  fontWeight: 500,
                  textTransform: "none",
                  fontSize: "0.95rem",
                  ":hover": { backgroundColor: "#7a1e49" },
                }}
                onClick={handleSearchByRut}
                disabled={loading || searchState.checking}
              >
                {loading || searchState.checking ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Buscar"
                )}
              </Button>
            </Box>

            {loading && !updatingStatuses && !searchState.checking && (
              <Box sx={{ textAlign: "center", my: 2 }}>
                <CircularProgress size={24} sx={{ color: "#99235c" }} />
              </Box>
            )}

            {!searchPerformed && (
              <Box
                sx={{
                  textAlign: "center",
                  mt: 6,
                  mb: 6,
                  py: 6,
                  backgroundColor: "#f9f9f9",
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                  <ErrorOutlineIcon
                    sx={{ fontSize: 100, color: "rgba(0, 0, 0, 0.15)" }}
                  />
                </Box>
                <Typography
                  sx={{
                    mt: 2,
                    color: "#555",
                    fontSize: "1.1rem",
                  }}
                >
                  Ingresa el RUT del cliente para consultar sus documentos
                </Typography>
              </Box>
            )}

            {searchState.checking && (
              <Box sx={{ textAlign: "center", my: 4 }}>
                <CircularProgress size={40} sx={{ color: "#99235c" }} />
                <Typography sx={{ mt: 2 }}>
                  Verificando información del cliente...
                </Typography>
              </Box>
            )}

            {error &&
              !searchState.notFoundInDb &&
              !searchState.foundInWisphub &&
              !searchState.notFoundAnywhere && (
                <Typography variant="body1" color="error" sx={{ my: 2 }}>
                  {error}
                </Typography>
              )}

            {searchState.foundInWisphub && (
              <ClientFoundInWisphubMessage
                clientData={searchState.clientData}
              />
            )}

            {searchState.notFoundAnywhere && <ClientNotFoundMessage />}

            {documents.length > 0 && (
              <Fade in={true} timeout={500}>
                <Paper
                  sx={{
                    p: 0,
                    mt: 3,
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 2,
                      pl: 3,
                      borderBottom: "1px solid #eee",
                      backgroundColor: "#fcfcfc",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontSize: "1.1rem",
                        fontWeight: 500,
                        color: "#333",
                      }}
                    >
                      Documentos encontrados
                      <Chip
                        label={filteredDocuments.length}
                        size="small"
                        sx={{
                          backgroundColor: "#99235c",
                          color: "white",
                          fontWeight: 600,
                        }}
                      />
                    </Typography>

                    <Tooltip title="Actualizar estados">
                      <IconButton
                        onClick={handleRefreshStatuses}
                        disabled={updatingStatuses}
                        sx={{
                          color: "#99235c",
                          "&:hover": {
                            backgroundColor: "rgba(153, 35, 92, 0.08)",
                          },
                        }}
                      >
                        {updatingStatuses ? (
                          <CircularProgress
                            size={20}
                            sx={{ color: "#99235c" }}
                          />
                        ) : (
                          <RefreshIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Box sx={{ overflowX: "auto" }}>
                    <Table sx={{ minWidth: 1000 }}>
                      <TableHead
                        sx={{
                          bgcolor: "#f8f9fa",
                          "& th": {
                            borderBottom: "1px solid #eaeaea",
                            fontWeight: 600,
                            padding: "16px",
                            color: "#333",
                            fontSize: "0.875rem",
                          },
                        }}
                      >
                        <TableRow>
                          <TableCell
                            width="48px"
                            align="center"
                            sx={{ padding: "16px 8px" }}
                          ></TableCell>
                          <TableCell width="25%">Código Documento</TableCell>
                          <TableCell width="15%" align="center">
                            Estado
                          </TableCell>
                          <TableCell width="20%" align="center">
                            Progreso
                          </TableCell>
                          <TableCell width="20%" align="center">
                            Fecha
                          </TableCell>
                          <TableCell width="20%" align="center">
                            Acciones
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredDocuments.map((doc) => (
                          <React.Fragment key={doc.id || doc.document_code}>
                            <TableRow
                              hover
                              sx={
                                doc.status === "Firmado"
                                  ? {
                                      bgcolor: "rgba(46, 125, 50, 0.04)",
                                    }
                                  : {}
                              }
                            >
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    toggleRowExpansion(doc.document_code)
                                  }
                                  sx={{
                                    color: expandedRows[doc.document_code]
                                      ? "#99235c"
                                      : "inherit",
                                  }}
                                >
                                  {expandedRows[doc.document_code] ? (
                                    <ExpandLessIcon fontSize="small" />
                                  ) : (
                                    <ExpandMoreIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                <Tooltip
                                  title="Ver detalles del documento"
                                  arrow
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    {isRecent(
                                      doc.lastUpdated || doc.created_at
                                    ) && (
                                      <Box
                                        component="span"
                                        sx={{
                                          width: 6,
                                          height: 6,
                                          borderRadius: "50%",
                                          bgcolor: "#99235c",
                                          display: "inline-block",
                                          mr: 1,
                                        }}
                                      />
                                    )}
                                    <Typography
                                      sx={{
                                        fontFamily: "monospace",
                                        letterSpacing: "0.5px",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {doc.document_code || "N/D"}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="center">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                  }}
                                >
                                  {getStatusIcon(doc.status)}
                                  <Chip
                                    label={doc.status || "Pendiente"}
                                    color={getStatusChipColor(doc.status)}
                                    size="small"
                                    sx={{ fontWeight: 500 }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                {doc.signingProgress ? (
                                  <Box sx={{ width: "100%", padding: "0 8px" }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexDirection: "column",
                                        gap: "4px",
                                      }}
                                    >
                                      <Box sx={{ width: "100%" }}>
                                        <LinearProgress
                                          variant="determinate"
                                          value={doc.signingProgress.percentage}
                                          sx={{
                                            height: 8,
                                            borderRadius: 4,
                                            bgcolor: "rgba(0, 0, 0, 0.05)",
                                            "& .MuiLinearProgress-bar": {
                                              borderRadius: 4,
                                              background:
                                                doc.status === "Firmado"
                                                  ? "linear-gradient(90deg, #1e8e3e 0%, #34a853 100%)"
                                                  : "linear-gradient(90deg, #ffa000 0%, #ffca28 100%)",
                                            },
                                          }}
                                        />
                                      </Box>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: "0.75rem",
                                          color:
                                            doc.status === "Firmado"
                                              ? "#1e8e3e"
                                              : "#ffa000",
                                        }}
                                      >{`${doc.signingProgress.signed}/${doc.signingProgress.total} firmantes (${doc.signingProgress.percentage}%)`}</Typography>
                                    </Box>
                                  </Box>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "rgba(0, 0, 0, 0.5)",
                                      fontStyle: "italic",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    No disponible
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#555" }}
                                >
                                  {formatDate(doc.sovos_created_at)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Tooltip title="Actualizar estado">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        updateSingleDocumentStatus(
                                          doc.document_code
                                        )
                                      }
                                      disabled={updatingStatuses}
                                      sx={{
                                        color: "#1976d2",
                                        backgroundColor:
                                          "rgba(25, 118, 210, 0.08)",
                                        "&:hover": {
                                          backgroundColor:
                                            "rgba(25, 118, 210, 0.12)",
                                        },
                                        padding: "8px",
                                      }}
                                    >
                                      <UpdateIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Ver documento">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleViewDocument(
                                          doc.document_code,
                                          searchRut
                                        )
                                      }
                                      sx={{
                                        color: "#99235c",
                                        backgroundColor:
                                          "rgba(153, 35, 92, 0.08)",
                                        "&:hover": {
                                          backgroundColor:
                                            "rgba(153, 35, 92, 0.12)",
                                        },
                                        padding: "8px",
                                      }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Descargar documento">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleDownload(
                                          doc.document_code,
                                          searchRut
                                        )
                                      }
                                      sx={{
                                        color: "#2e7d32",
                                        backgroundColor:
                                          "rgba(46, 125, 50, 0.08)",
                                        "&:hover": {
                                          backgroundColor:
                                            "rgba(46, 125, 50, 0.12)",
                                        },
                                        padding: "8px",
                                      }}
                                    >
                                      <FileDownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>

                            {/* Fila expandida con detalles */}
                            {expandedRows[doc.document_code] && (
                              <TableRow sx={{ bgcolor: "rgba(0, 0, 0, 0.02)" }}>
                                <TableCell colSpan={9} sx={{ p: 0 }}>
                                  <Fade in={true} timeout={400}>
                                    <Box sx={{ py: 2, px: 4 }}>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                          <Typography
                                            variant="subtitle2"
                                            color="text.secondary"
                                            sx={{
                                              mb: 1.5,
                                              fontWeight: 600,
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 0.5,
                                            }}
                                          >
                                            <InfoIcon
                                              fontSize="small"
                                              sx={{ fontSize: "1rem" }}
                                            />
                                            Detalles del Documento
                                          </Typography>
                                          <Divider sx={{ my: 1 }} />
                                          <Stack spacing={1.5}>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontWeight: 500 }}
                                              >
                                                Código:
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                sx={{ fontFamily: "monospace" }}
                                              >
                                                {doc.document_code}
                                              </Typography>
                                            </Box>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontWeight: 500 }}
                                              >
                                                Tipo:
                                              </Typography>
                                              <Typography variant="body2">
                                                {doc.contract_type || "Alta"}
                                              </Typography>
                                            </Box>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontWeight: 500 }}
                                              >
                                                Estado:
                                              </Typography>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 0.5,
                                                }}
                                              >
                                                {getStatusIcon(doc.status)}
                                                <Typography variant="body2">
                                                  {doc.status || "Pendiente"}
                                                </Typography>
                                              </Box>
                                            </Box>
                                            {doc.sovos_created_at && (
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent:
                                                    "space-between",
                                                }}
                                              >
                                                <Typography
                                                  variant="body2"
                                                  color="text.secondary"
                                                  sx={{ fontWeight: 500 }}
                                                >
                                                  Fecha de documento:
                                                </Typography>
                                                <Typography variant="body2">
                                                  {formatDate(
                                                    doc.sovos_created_at
                                                  )}
                                                </Typography>
                                              </Box>
                                            )}
                                          </Stack>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                          <Typography
                                            variant="subtitle2"
                                            color="text.secondary"
                                            sx={{
                                              mb: 1.5,
                                              fontWeight: 600,
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 0.5,
                                            }}
                                          >
                                            <AccessTimeIcon
                                              fontSize="small"
                                              sx={{ fontSize: "1rem" }}
                                            />
                                            Información de Firma
                                          </Typography>
                                          <Divider sx={{ my: 1 }} />
                                          <Stack spacing={1.5}>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontWeight: 500 }}
                                              >
                                                Progreso:
                                              </Typography>
                                              <Typography variant="body2">
                                                {doc.signingProgress
                                                  ? `${doc.signingProgress.signed}/${doc.signingProgress.total} firmantes (${doc.signingProgress.percentage}%)`
                                                  : "No disponible"}
                                              </Typography>
                                            </Box>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontWeight: 500 }}
                                              >
                                                Última actualización:
                                              </Typography>
                                              <Typography variant="body2">
                                                {doc.lastUpdated
                                                  ? formatDate(doc.lastUpdated)
                                                  : "No disponible"}
                                              </Typography>
                                            </Box>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontWeight: 500 }}
                                              >
                                                Tiempo desde actualización:
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  color: isRecent(
                                                    doc.lastUpdated
                                                  )
                                                    ? "#99235c"
                                                    : "inherit",
                                                  fontWeight: isRecent(
                                                    doc.lastUpdated
                                                  )
                                                    ? 500
                                                    : 400,
                                                }}
                                              >
                                                {formatRelativeTime(
                                                  doc.lastUpdated
                                                )}
                                              </Typography>
                                            </Box>
                                          </Stack>
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  </Fade>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>

                  {filteredDocuments.length === 0 && documents.length > 0 && (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No hay documentos que coincidan con los criterios de
                        búsqueda.
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Fade>
            )}
          </ContentContainer>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Componente Grid importado localmente para no añadir dependencias
const Grid = ({ container, item, xs, md, children, spacing, sx }) => {
  const gridStyles = {
    ...(container && {
      display: "flex",
      flexWrap: "wrap",
      margin: spacing ? -spacing / 2 : 0,
    }),
    ...(item && {
      padding: spacing ? spacing / 2 : 0,
      flexBasis: xs === 12 ? "100%" : xs === 6 ? "50%" : "auto",
      [Object.keys(sx || {}).length > 0 && "sx"]: sx,
    }),
  };

  return <Box sx={gridStyles}>{children}</Box>;
};

export default DocumentStatusPage;
